import { useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Modal, Form, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { toast } from 'react-toastify';

interface Organization {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    tickets: number;
  };
}

export const OrganizationsPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    domain: '',
    status: 'active'
  });

  const queryClient = useQueryClient();

  // Fetch organizations
  const { data: orgsData, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await api.get('/admin/organizations');
      return response.data;
    }
  });

  // Create organization mutation
  const createOrgMutation = useMutation({
    mutationFn: async (orgData: Partial<Organization>) => {
      const response = await api.post('/admin/organizations', orgData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization created successfully');
      setShowModal(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create organization');
    }
  });

  // Update organization mutation
  const updateOrgMutation = useMutation({
    mutationFn: async ({ id, ...orgData }: Partial<Organization> & { id: string }) => {
      const response = await api.patch(`/admin/organizations/${id}`, orgData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization updated successfully');
      setShowModal(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update organization');
    }
  });

  // Delete organization mutation
  const deleteOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/organizations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete organization');
    }
  });

  const resetForm = () => {
    setFormData({ name: '', slug: '', domain: '', status: 'active' });
    setEditingOrg(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      slug: org.slug,
      domain: org.domain || '',
      status: org.status
    });
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      deleteOrgMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingOrg) {
      updateOrgMutation.mutate({ id: editingOrg.id, ...formData });
    } else {
      createOrgMutation.mutate(formData);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name: string) => {
    setFormData({ ...formData, name, slug: generateSlug(name) });
  };

  const organizations = orgsData?.data || [];

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <i className="fas fa-building me-2 text-primary"></i>
                Organizations
              </h2>
              <p className="text-muted mb-0">Manage organizations and their settings</p>
            </div>
            <Button
              variant="primary"
              onClick={handleCreate}
              className="d-flex align-items-center"
            >
              <i className="fas fa-plus me-2"></i>
              Add Organization
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Organizations ({organizations.length})
                </h5>
              </div>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading organizations...</span>
                  </Spinner>
                </div>
              ) : organizations.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-building fs-1 text-muted mb-3"></i>
                  <h5 className="text-muted">No organizations found</h5>
                  <p className="text-muted">Create your first organization to get started.</p>
                  <Button variant="primary" onClick={handleCreate}>
                    <i className="fas fa-plus me-2"></i>
                    Add Organization
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Slug</th>
                        <th>Domain</th>
                        <th>Status</th>
                        <th>Users</th>
                        <th>Tickets</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {organizations.map((org: Organization) => (
                        <tr key={org.id}>
                          <td>
                            <div className="fw-semibold">{org.name}</div>
                          </td>
                          <td>
                            <code className="text-muted">{org.slug}</code>
                          </td>
                          <td>
                            <div className="text-muted">
                              {org.domain || 'No domain set'}
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${org.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                              {org.status}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-info">
                              {org._count?.users || 0}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-warning">
                              {org._count?.tickets || 0}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(org.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(org)}
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(org.id)}
                                disabled={deleteOrgMutation.isPending}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-building me-2"></i>
            {editingOrg ? 'Edit Organization' : 'Create Organization'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-building me-2 text-primary"></i>
                    Organization Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter organization name"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-link me-2 text-info"></i>
                    Slug *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="organization-slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Used in URLs and API calls
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-globe me-2 text-success"></i>
                    Domain
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="example.com"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Optional: Custom domain for this organization
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <i className="fas fa-toggle-on me-2 text-warning"></i>
                    Status
                  </Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createOrgMutation.isPending || updateOrgMutation.isPending}
            >
              {(createOrgMutation.isPending || updateOrgMutation.isPending) && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              {editingOrg ? 'Update Organization' : 'Create Organization'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};